/**
 * Settings → Calling — how outbound calls are placed from CleanBid.
 *
 * Direct-first product model (Quo / RingCentral style):
 *
 *   Direct call (DEFAULT) — the customer's phone rings instantly and sees
 *     your CleanBid business number. Nothing rings your personal phone.
 *
 *   Ring my phone first (EXPLICIT fallback) — CleanBid calls your phone,
 *     you answer, then we connect the customer and bridge the two legs.
 *     The customer still sees your CleanBid business number.
 *
 * Persisted via `/api/users/dial-preference`:
 *   ring-my-phone ON  → 'phone'
 *   ring-my-phone OFF → 'native'  (direct — the default)
 */
import React, { useState } from 'react';
import { Phone, Zap, Loader2 } from 'lucide-react';
import useDialPreference from '../../hooks/useDialPreference';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function CallingSettings() {
  const [saving, setSaving] = useState(false);
  const { preferBridge, ready, saveRingMyPhone } = useDialPreference();

  const onToggle = async (next) => {
    if (saving || next === preferBridge) return;
    setSaving(true);
    try {
      await saveRingMyPhone(next);
      toast.success(
        next
          ? 'Calls will ring your phone first, then connect the customer'
          : 'Calls will ring the customer directly from your business number'
      );
    } catch (err) {
      toast.error(err?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-testid="calling-settings-section">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
        <Phone className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-800">Calling</h3>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Default behaviour — direct calling */}
        <div className="flex items-start gap-3">
          <Zap className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-gray-800">Direct call</p>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Default. Tap Call and the customer&apos;s phone rings instantly from
              your CleanBid business number — your personal phone is never
              involved.
            </p>
          </div>
        </div>

        {/* Explicit fallback opt-in */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-100">
          <div className="flex items-start gap-3">
            <Phone className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <Label
                htmlFor="ring-my-phone-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                Ring my phone first
              </Label>
              <p className="text-xs text-gray-500 mt-1 max-w-md">
                Fallback. CleanBid calls your phone, you pick up, then we
                connect the customer. Use this if you&apos;d rather talk on your
                phone than in the browser. Customers still see your CleanBid
                number.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-1">
            {(saving || !ready) && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
            <Switch
              id="ring-my-phone-toggle"
              checked={preferBridge}
              onCheckedChange={onToggle}
              disabled={saving || !ready}
              data-testid="ring-my-phone-toggle"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
